import { EditOutlined } from "@ant-design/icons"
import { BackButton } from "components/BackButton/BackButton"
import { Button } from "components/Button/Button"
import { Divider } from "components/Divider/Divider"
import { Form } from "components/Form/Form"
import { useNotification } from "components/Notification/Notification"
import { Spinner } from "components/Spinner/Spinner"
import { useDisclosure } from "hooks/useDisclosure"
import { useSpotlight } from "hooks/useSpotlight"
import { Chat } from "modules/communications/Chat"
import { Voice } from "modules/communications/Voice"
import { AlternateContactsList } from "modules/patient/AlternateContactsList"
import { CancelVisit } from "modules/visits/CancelVisit"
import { Comments } from "modules/visits/Comments"
import { EditVisit } from "modules/visits/EditVisit"
import { ComponentProps, ReactNode } from "react"
import { useNavigate, useParams } from "react-router"
import { useTeam } from "services/users"
import { useVisit } from "services/visits"
import { cn } from "styles/cn"
import { DEFAULT_PLACEHOLDER } from "utils/constants"
import { asDate } from "utils/date"
import { prettyprintPhoneNumber } from "utils/patient"

export function UnscheduledVisit() {
	const { scopeId, visitId } = useParams()
	const navigate = useNavigate()
	const { notify } = useNotification()
	const { data: visit, isFetching, isFetched } = useVisit(visitId!)
	const team = useTeam()
	const discloseCancel = useDisclosure()
	const discloseEdit = useDisclosure()
	const { focus } = useSpotlight()
	const {
		address = DEFAULT_PLACEHOLDER,
		email = DEFAULT_PLACEHOLDER,
		fullName = DEFAULT_PLACEHOLDER,
		phoneNumber,
		alternateContacts,
		hasCommunications,
	} = visit ? (visit.patient ?? {}) : {}
	const handleNavigateToUnscheduledVisits = () =>
		navigate(`/${scopeId}/visits/unscheduled`)
	const handleScheduleVisitSuccess = () => {
		notify.success({
			message: "Schedule visit",
			description: "The visit was scheduled successfully",
		})

		focus("all-visits-tab")

		handleNavigateToUnscheduledVisits()
	}
	const handleCancelledVisitRequest = () => {
		notify.success({
			message: "Cancel visit request",
			description: "The visit was cancelled successfully",
		})

		handleNavigateToUnscheduledVisits()
	}

	if (!visit && isFetched) {
		handleNavigateToUnscheduledVisits()
	}

	if (!team || !visit || !visit.patient) {
		return null
	}

	return (
		<Spinner spinning={isFetching}>
			<div className="@container mx-auto flex h-full max-h-full max-w-300 flex-col gap-6 overflow-hidden">
				<div className="flex items-center justify-between gap-2">
					<BackButton onClick={handleNavigateToUnscheduledVisits}>
						Needs scheduling
					</BackButton>

					<Button onClick={discloseCancel.open}>Cancel visit request</Button>

					{discloseCancel.isOpen && visit && (
						<CancelVisit
							visit={{
								id: visit.id,
								status: "CANCELLED",
								cancelDate: asDate().toISOString(),
							}}
							open={discloseCancel.isOpen}
							onCancel={discloseCancel.close}
							onSuccess={handleCancelledVisitRequest}
						>
							If you cancel the visit request, the patient will not be scheduled
							for visits.
						</CancelVisit>
					)}
				</div>

				<div className="size-full overflow-y-auto">
					<div className="grid grid-cols-1 divide-grey-20 overflow-clip rounded-2xl border border-grey-20 @4xl:grid-cols-2 @4xl:divide-x">
						<div className="@container/details relative flex flex-col gap-6 bg-mint-0 px-5 py-8 @4xl:px-16 @4xl:py-12">
							<Button
								type="link"
								icon={<EditOutlined aria-hidden />}
								className="absolute top-2 right-2 @4xl:top-6 @4xl:right-6"
								onClick={discloseEdit.open}
							>
								Edit information
							</Button>

							{discloseEdit.isOpen && visit && (
								<EditVisit.Modal
									open={discloseEdit.isOpen}
									onCancel={discloseEdit.close}
								>
									<EditVisit.Form
										visit={visit}
										fields={["patient"]}
										onSuccess={() => {
											notify.success({
												message: "Edit information",
												description:
													"The patient's information was updated successfully",
											})
											discloseEdit.close()
										}}
									/>
								</EditVisit.Modal>
							)}

							<h2 className="truncate font-nunito text-2xl font-extrabold">
								{fullName}
							</h2>

							<dl className="flex flex-col gap-1">
								{(
									[
										["Address:", address, ""],
										[
											"Phone number:",
											prettyprintPhoneNumber(phoneNumber, hasCommunications),
											"truncate",
										],
										["Email:", email, "truncate"],
										[
											"Alternate contacts:",
											<AlternateContactsList
												alternateContacts={alternateContacts}
												permission={team.manageContacts}
											/>,
											"",
										],
									] as [string, ReactNode, string][]
								).map(([label, value, className]) => (
									<div
										key={label}
										className="flex w-full justify-between gap-2"
									>
										<dd className="text-navy-60">{label}</dd>
										<dd className={cn("text-right", className)}>{value}</dd>
									</div>
								))}
							</dl>

							<div className="flex flex-col items-center justify-center gap-2 @md/details:flex-row @md/details:gap-4">
								<Voice.CallToAction
									className="w-full"
									patient={visit.patient}
								/>

								<Chat.CallToAction
									view
									send
									className="w-full"
									patient={visit.patient}
									unreadMessagesCount={visit.unreadMessagesCount}
								/>
							</div>

							<Divider className="border-grey-20" />

							<Comments visit={visit} />
						</div>

						<div className="flex flex-col gap-8 px-5 py-8 @4xl:px-16 @4xl:py-12">
							<h2 className="truncate font-nunito text-lg font-bold">
								Schedule visit for {fullName}
							</h2>

							{visit && (
								<ScheduleVisit
									visit={visit}
									ariaLabel={`Schedule visit for ${fullName}`}
									onSuccess={handleScheduleVisitSuccess}
								/>
							)}
						</div>
					</div>
				</div>
			</div>
		</Spinner>
	)
}

function ScheduleVisit(props: ComponentProps<typeof EditVisit.Form>) {
	const [form] = Form.useForm()

	return (
		<EditVisit.Form
			{...props}
			fields={["agentUserIds", "notes", "visitDate"]}
			form={form}
		>
			{({ isSaving }) => (
				<Form.Item className="pt-2">
					<Button
						type="primary"
						htmlType="button"
						loading={isSaving}
						className="w-full"
						onClick={form.submit}
					>
						Schedule visit
					</Button>
				</Form.Item>
			)}
		</EditVisit.Form>
	)
}
